import { FC, useEffect, useState } from "react";
import { LocationMarkerIcon } from "@heroicons/react/solid";
import axios from "axios";
import LocationMarker from "components/AnyReactComponent/LocationMarker";
import Label from "components/Label/Label";
import GoogleMapReact from "google-map-react";
import Select from "shared/Select/Select";
import Textarea from "shared/Textarea/Textarea";
import FormItem from "containers/PageAddListing1/FormItem";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { AddressType } from "data/types";
import emptyAddress from "data/empty/address.json";
import { RentripsURL } from "utils/request";

interface AddressProps {
	modify?: boolean;
	dataz?: AddressType;
	handleClose?: () => void;
	handleSubmit?: (address: AddressType) => void;
	handleModify?: (address: AddressType) => void;
}

const Address: FC<AddressProps> = ({
	modify = false,
	dataz = emptyAddress,
	handleClose = () => {},
	handleSubmit = () => {},
	handleModify = () => {},
}) => {
	const id = dataz?._id || "";
	const [name, setName] = useState<string>(dataz.name);
	const [recipient, setRecipient] = useState<string>(dataz.recipient);
	const [fullAddress, setFullAddress] = useState<string>(dataz.fullAddress);
	const [provinsiList, setProvinsiList] = useState<[] | undefined>([]);
	const [kabupatenList, setKabupatenList] = useState<[] | undefined>([]);
	const [kecamatanList, setKecamatanList] = useState<[] | undefined>([]);
	const [kelurahanList, setKelurahanList] = useState<[] | undefined>([]);
	const [provinsi, setProvinsi] = useState<{ code: string; name: string }>(
		dataz.provinsi
	);
	const [kabupaten, setKabupaten] = useState<{ code: string; name: string }>(
		dataz.kabupaten
	);
	const [kecamatan, setKecamatan] = useState<{ code: string; name: string }>(
		dataz.kecamatan
	);
	const [kelurahan, setKelurahan] = useState<{ code: string; name: string }>(
		dataz.kelurahan
	);
	const [latitude, setLatitude] = useState<number>(dataz.map.latitude);
	const [longitude, setLongitude] = useState<number>(dataz.map.longitude);
	const [zipCode, setZipCode] = useState<string>(dataz.zipCode);
	const [phone, setPhone] = useState<string>(dataz.phone);

	const submitHandler = (e: any) => {
		handleSubmit({
			name,
			fullAddress,
			recipient,
			provinsi,
			kabupaten,
			kecamatan,
			kelurahan,
			zipCode: zipCode,
			map: {
				latitude,
				longitude,
			},
			phone,
		});
		e.preventDefault();
	};

	const getLocation = () => {
		navigator.geolocation.getCurrentPosition(success, error);

		function error(error: any) {
			return;
		}

		function success(pos: any) {
			var crd = pos.coords;
			setLatitude(crd.latitude);
			setLongitude(crd.longitude);
		}
	};

	useEffect(() => {
		const getProvinsi = async () => {
			const { data } = await axios.get(
				RentripsURL + "/api/address/region/provinsi"
			);
			setProvinsiList(data);
		};
		getProvinsi();
	}, []);

	useEffect(() => {
		const getKabupaten = async () => {
			const { data: kabupatens } = await axios.get(
				RentripsURL + `/api/address/region/kabupaten?id=${provinsi.code}`
			);

			setKabupatenList(kabupatens);
			if (!dataz) setKabupaten(kabupatens[0]);

			const { data: kecamatans } = await axios.get(
				RentripsURL + `/api/address/region/kecamatan?id=${kabupatens[0].code}`
			);
			if (!dataz) setKecamatanList(kecamatans);
			if (!dataz) setKecamatan(kecamatans[0]);

			const { data: kelurahans } = await axios.get(
				RentripsURL + `/api/address/region/kelurahan?id=${kecamatans[0].code}`
			);
			if (!dataz) setKelurahanList(kelurahans);
			if (!dataz) setKelurahan(kelurahans[0]);
		};
		getKabupaten();
	}, [provinsi]);

	useEffect(() => {
		const getKecamatan = async () => {
			const { data: kecamatans } = await axios.get(
				RentripsURL + `/api/address/region/kecamatan?id=${kabupaten.code}`
			);
			setKecamatanList(kecamatans);
			if (!dataz) setKecamatan(kecamatans[0]);

			const { data: kelurahans } = await axios.get(
				RentripsURL + `/api/address/region/kelurahan?id=${kecamatans[0].code}`
			);
			if (!dataz) setKelurahanList(kelurahans);
			if (!dataz) setKelurahan(kelurahans[0]);
		};
		getKecamatan();
	}, [kabupaten]);

	useEffect(() => {
		const getKelurahan = async () => {
			const { data } = await axios.get(
				RentripsURL + `/api/address/region/kelurahan?id=${kecamatan.code}`
			);
			setKelurahanList(data);
		};
		getKelurahan();
	}, [kecamatan]);

	return (
		<form className="space-y-8 dark:text-white" onSubmit={submitHandler}>
			{/* ITEM */}
			<div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-5 ">
				<FormItem label="Nama Alamat">
					<Input
						placeholder="Namai Tempatmu"
						value={name}
						onChange={(e) => setName(e.target.value)}
						required={true}
					></Input>
				</FormItem>
				<FormItem label="Nama Penerima">
					<Input
						placeholder="Nama Penerima Barang"
						value={recipient}
						onChange={(e) => setRecipient(e.target.value)}
						required={true}
					></Input>
				</FormItem>
			</div>
			<FormItem label="Alamat lengkap">
				<Textarea
					placeholder="Masukkan alamat lengkap anda"
					value={fullAddress}
					onChange={(e) => setFullAddress(e.target.value)}
					required={true}
				/>
			</FormItem>

			<div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-5">
				<FormItem label="Provinsi">
					<Select
						onChange={(e) => {
							const optionArray = [...e.target.options];
							const provinsiName = optionArray.find(
								(value) => value.value === e.target.value
							)?.innerHTML;
							setProvinsi({
								code: e.target.value,
								name: provinsiName as string,
							});
						}}
						required={true}
						value={provinsi.code}
					>
						{provinsiList &&
							provinsiList.map((ket: { code: number; name: string }) => {
								return (
									<option key={ket.code} value={ket.code}>
										{ket.name}
									</option>
								);
							})}
					</Select>
				</FormItem>
				<FormItem label="Kabupaten/Kota">
					<Select
						onChange={(e) => {
							const optionArray = [...e.target.options];
							const kabupatenName = optionArray.find(
								(value) => value.value === e.target.value
							)?.innerHTML;
							setKabupaten({
								code: e.target.value,
								name: kabupatenName as string,
							});
						}}
						required={true}
						value={kabupaten.code}
					>
						{kabupatenList &&
							kabupatenList.map((ket: { code: number; name: string }) => {
								return (
									<option key={ket.code} value={ket.code}>
										{ket.name}
									</option>
								);
							})}
					</Select>
				</FormItem>
				<FormItem label="Kecamatan">
					<Select
						onChange={(e) => {
							const optionArray = [...e.target.options];
							const kecamatanName = optionArray.find(
								(value) => value.value === e.target.value
							)?.innerHTML;
							setKecamatan({
								code: e.target.value,
								name: kecamatanName as string,
							});
						}}
						required={true}
						value={kecamatan.code}
					>
						{kecamatanList &&
							kecamatanList.map((ket: { code: number; name: string }) => {
								return (
									<option key={ket.code} value={ket.code}>
										{ket.name}
									</option>
								);
							})}
					</Select>
				</FormItem>
				<FormItem label="Kelurahan">
					<Select
						onChange={(e) => {
							const optionArray = [...e.target.options];
							const kelurahanName = optionArray.find(
								(value) => value.value === e.target.value
							)?.innerHTML;
							setKelurahan({
								code: e.target.value,
								name: kelurahanName as string,
							});
						}}
						required={true}
						value={kelurahan.code}
					>
						{kelurahanList &&
							kelurahanList.map((ket: { code: number; name: string }) => {
								return (
									<option key={ket.code} value={ket.code}>
										{ket.name}
									</option>
								);
							})}
					</Select>
				</FormItem>
				<FormItem label="Kode Pos">
					<Input
						type="number"
						placeholder="Kode Pos Anda"
						value={zipCode}
						required={true}
						onChange={(e) => setZipCode(e.target.value)}
					/>
				</FormItem>
				<FormItem label="Nomor Telepon">
					<div className="flex items-center">
						<span className="text-sm mr-2">+62</span>
						<Input
							type="number"
							placeholder="8123xxx"
							minLength={8}
							value={phone}
							onChange={(e) => setPhone(e.target.value)}
							required={true}
						/>
					</div>
				</FormItem>
			</div>

			<div>
				<Label className="block">Lokasi pada peta</Label>
				<ButtonSecondary className="mt-3" onClick={getLocation} type="button">
					<LocationMarkerIcon className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
					<span className="ml-3">Pilih Lokasi Sekarang</span>
				</ButtonSecondary>
				<div className="mt-4">
					<div className="aspect-w-5 aspect-h-5 sm:aspect-h-3">
						<div className="rounded-xl overflow-hidden">
							<GoogleMapReact
								bootstrapURLKeys={{
									key: "AIzaSyDxJaU8bLdx7sSJ8fcRdhYS1pLk8Jdvnx0",
								}}
								defaultZoom={15}
								yesIWantToUseGoogleMapApiInternals
								defaultCenter={{
									lat: -8.746325945216654,
									lng: 115.16803037857355,
								}}
								center={{ lat: latitude, lng: longitude }}
								draggable={true}
								onDrag={(coord) => {
									setLatitude(coord.latitude);
									setLongitude(coord.longitude);
								}}
							>
								<LocationMarker lat={latitude} lng={longitude} />
							</GoogleMapReact>
						</div>
					</div>
				</div>
				{modify ? (
					<div className="flex justify-end mt-3">
						<ButtonSecondary className="mr-3" onClick={() => handleClose()}>
							Tutup
						</ButtonSecondary>
						<ButtonPrimary
							type="button"
							onClick={() =>
								handleModify({
									_id: id,
									name,
									recipient,
									provinsi,
									kabupaten,
									kecamatan,
									kelurahan,
									zipCode: zipCode,
									map: {
										latitude,
										longitude,
									},
									phone,
									fullAddress,
								})
							}
						>
							Ubah
						</ButtonPrimary>
					</div>
				) : (
					<div className="flex justify-end mt-3">
						<ButtonSecondary className="mr-3" onClick={() => handleClose()}>
							Tutup
						</ButtonSecondary>
						<ButtonPrimary type="submit">Simpan</ButtonPrimary>
					</div>
				)}
			</div>
		</form>
	);
};

export default Address;
